import { RUNNING_BUILD, isRelease } from './deployedVersion';
import { userStore } from '../../../utils/userStorage';

/*
 * Which build this browser has already seen.
 *
 * The counterpart of the update notice: that one says a newer release
 * *is waiting*, this one notices that it *has arrived* — the reload
 * happened, and whoever caused it deserves to be told what changed
 * instead of having to look for the dashboard card.
 *
 * Unlike the pending-update state (deliberately memory-only, see
 * VersionUpdateContext), this has to survive the reload: it is the
 * reload it is about.
 *
 * The very first visit announces nothing. Nobody arriving for the
 * first time has anything to catch up on, and a modal dialog would be
 * the worst possible greeting.
 *
 * Compared is the build stamp — commit *and* build time, see
 * RUNNING_BUILD —, not the commit alone: `tools/remote` deploys the
 * working tree, so a second deployment of the same sha is a different
 * build, and used to pass for the one already seen.
 */
const STORAGE_KEY = 'seenBuild';

/* filed under the logged-in user, as everything the browser remembers
   for this app is — see utils/userStorage */
function stored() {
  return userStore.getItem(STORAGE_KEY);
}

/*
 * Asking must stay free of consequences.
 *
 * The build used to be noted as soon as the dialog appeared, and that
 * is what made it disappear again: the tree behind the login comes and
 * goes — a background token renewal alone takes it down and puts it
 * back up (see ProtectedRoute) — and the second mounting asked the
 * same question, only to hear "nothing new" from the note the first
 * one had left. What is noted now is that the news were *read*.
 */
export function isNewBuild(running = RUNNING_BUILD) {
  /* a dev server never changes its build, so there is nothing to
     announce there either */
  if (!isRelease(running)) return false;
  const seen = stored();
  return Boolean(seen) && seen !== running;
}

export function rememberBuild(running = RUNNING_BUILD) {
  if (!isRelease(running)) return;
  userStore.setItem(STORAGE_KEY, running);
}

/* Nobody arriving for the first time has anything to catch up on — but
   the build they arrive on has to be written down all the same, or the
   next visit would be a first visit too and stay just as quiet. */
export function isFirstVisit() {
  return !stored();
}
